import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  Search,
  GripVertical,
  Building2,
  BookOpen,
  Users,
  Filter,
  Layers,
  AlertCircle,
  CheckCircle,
  Loader2,
  X,
  BarChart3,
} from 'lucide-react';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { RoomOccupancyButton } from '../components/room-occupancy/RoomOccupancyButton';
import { Permission } from '@/types/global.types';
import { fetchFilieresGrouped, getSemestersByFiliere, semesterOptionId, optionsForNiveau } from '@/services/filieresService';
import { getAvailableCampuses, getGroupesByFiliere } from '@/services/groupsService';
import { fetchTeachingRooms, type RoomFlat } from '@/services/campusInfrastructureService';
import { fetchAcademicYears, AcademicYear } from '@/services/academicPlanningService';
import { fetchStaffUsers, StaffUser } from '@/services/userService';
import { calendarSettingsService, SessionType } from '../services/calendarSettingsService';
import { scheduleService, ScheduleEntry, ScheduleSession, CreateScheduleEntryDto, BlockedDate, RamadanPeriod, scheduledHoursByMatiere } from '../services/scheduleService';
import type { FiliereWithOptions, SemesterWithModules, ModuleElement } from '@/types/filiere.types';
import type { GroupeWithCounts } from '@/types/group.types';

// Import the new components
import { TimetableGrid, CanceledSession } from '../components/timetable/TimetableGrid';
import { SessionTypeModal } from '../components/timetable/SessionTypeModal';
import { EditSessionModal } from '../components/timetable/EditSessionModal';
import { ConflictResolutionModal } from '../components/timetable/ConflictResolutionModal';
import { VolumeExceededModal } from '../components/timetable/VolumeExceededModal';
import { SchedulingProgressPanel } from '../components/timetable/SchedulingProgressPanel';
import type { ConflictCheckResult, VolumeCheck } from '../services/scheduleService';

interface Campus {
  id: string;
  name: string;
  sigle: string | null;
  isMainCampus: boolean;
}

// Helper to determine current semester based on academic year dates
function getCurrentSemester(academicYear: AcademicYear | null): 1 | 2 {
  if (!academicYear) return 1;

  const now = new Date();

  // Primary check: use semester2StartDate as the definitive boundary
  if (academicYear.semester2StartDate) {
    const semester2Start = new Date(academicYear.semester2StartDate);
    if (now >= semester2Start) {
      return 2;
    }
    return 1;
  }

  // Fallback: use semester1EndDate
  if (academicYear.semester1EndDate) {
    const semester1End = new Date(academicYear.semester1EndDate);
    if (now > semester1End) {
      return 2;
    }
    return 1;
  }

  // Last resort fallback: after mid-February = S2
  const month = now.getMonth();
  return month >= 1 && month <= 6 ? 2 : 1;
}

export const TimetableManagementPage: React.FC = () => {
  const { tokens } = useAuthStore();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.SCHEDULE_MANAGE);

  // UI State
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Data State
  const [academicYears, setAcademicYears] = useState<AcademicYear[]>([]);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState<AcademicYear | null>(null);
  const [campuses, setCampuses] = useState<Campus[]>([]);
  const [hasMultipleCampuses, setHasMultipleCampuses] = useState(false);
  const [selectedCampusId, setSelectedCampusId] = useState<string | null>(null);
  const [filieres, setFilieres] = useState<FiliereWithOptions[]>([]);
  const [selectedFiliereId, setSelectedFiliereId] = useState<string | null>(null);
  // Option of the filière at the selected niveau (specialisation years only)
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [filiereSemesters, setFiliereSemesters] = useState<SemesterWithModules[]>([]);
  const [niveaux, setNiveaux] = useState<number[]>([]);
  const [selectedNiveau, setSelectedNiveau] = useState<number | null>(null);
  const [groupes, setGroupes] = useState<GroupeWithCounts[]>([]);
  const [selectedGroupeId, setSelectedGroupeId] = useState<string | null>(null);
  const [selectedSemester, setSelectedSemester] = useState<1 | 2>(1);
  const [sessionTypes, setSessionTypes] = useState<SessionType[]>([]);

  // Matières (module elements) for the selected group/semester
  const [, setSemesters] = useState<SemesterWithModules[]>([]);
  const [matieres, setMatieres] = useState<ModuleElement[]>([]);
  const [loadingMatieres, setLoadingMatieres] = useState(false);

  // Professors for session assignment
  const [professors, setProfessors] = useState<StaffUser[]>([]);
  const [, setLoadingProfessors] = useState(false);

  // Rooms for session assignment
  const [rooms, setRooms] = useState<RoomFlat[]>([]);
  const [, setLoadingRooms] = useState(false);

  // Schedule entries
  const [entries, setEntries] = useState<ScheduleEntry[]>([]);
  const [loadingEntries, setLoadingEntries] = useState(false);

  // Blocked dates (holidays & vacations) for current week view
  const [blockedDates, setBlockedDates] = useState<BlockedDate[]>([]);

  // Ramadan period
  const [ramadanPeriod, setRamadanPeriod] = useState<RamadanPeriod | null>(null);

  // Semester-wide blocked dates for session planning calculation
  const [semesterBlockedDates, setSemesterBlockedDates] = useState<BlockedDate[]>([]);

  // Canceled sessions for the current week
  const [canceledSessions, setCanceledSessions] = useState<CanceledSession[]>([]);

  // Week navigation
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    return new Date(today.setDate(diff)).toISOString().split('T')[0];
  });

  // Drag and drop state
  const [draggedMatiere, setDraggedMatiere] = useState<ModuleElement | null>(null);
  const [dropTarget, setDropTarget] = useState<{ dayOfWeek: number; startTime: string } | null>(null);

  // Session type modal
  const [showSessionTypeModal, setShowSessionTypeModal] = useState(false);
  const [pendingScheduleData, setPendingScheduleData] = useState<{
    matiere: ModuleElement;
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    date: string; // Concrete date of the clicked cell (YYYY-MM-DD) — sessions start from this week
    outOfSemester?: boolean; // User confirmed scheduling outside the semester window
  } | null>(null);

  // Out-of-semester confirmation prompt (shown before the session modal)
  const [outOfSemesterPrompt, setOutOfSemesterPrompt] = useState<{
    matiere: ModuleElement;
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    date: string;
  } | null>(null);

  // Edit session modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<ScheduleEntry | null>(null);
  const [selectedSessionDate, setSelectedSessionDate] = useState<string | null>(null);

  // Conflict resolution modal state
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictResult, setConflictResult] = useState<ConflictCheckResult | null>(null);
  const [pendingCreateData, setPendingCreateData] = useState<{
    sessionTypeId: string;
    selectedMatiereId?: string;
    selectedProfessorId?: string;
    recurrenceMode?: 'single' | 'until_volume' | 'custom';
    recurrenceWeeks?: number;
    roomId?: string;
    ramadanStartTime?: string;
    ramadanEndTime?: string;
    paidDurationMinutes?: number;
  } | null>(null);
  const [isCreatingEntry, setIsCreatingEntry] = useState(false);
  // A click is being processed (conflict check or creation): further clicks are ignored
  const [isSubmittingEntry, setIsSubmittingEntry] = useState(false);
  const submitInFlightRef = useRef(false);
  // Volume horaire confirmation (shown when the plan goes beyond the subject's hours)
  const [volumeConfirm, setVolumeConfirm] = useState<{ volume: VolumeCheck; matiereName: string } | null>(null);

  // Progress panel state
  const [showProgressPanel, setShowProgressPanel] = useState(false);

  // Load initial data
  const loadInitialData = useCallback(async () => {
    if (!tokens?.accessToken) return;

    setIsLoading(true);
    setError(null);

    try {
      const [yearsData, campusData, filieresData, sessionTypesData] = await Promise.all([
        fetchAcademicYears(tokens.accessToken),
        getAvailableCampuses(),
        fetchFilieresGrouped(true),
        calendarSettingsService.getSessionTypes(),
      ]);

      setAcademicYears(yearsData);
      setCampuses(campusData.campuses);
      setHasMultipleCampuses(campusData.hasMultipleCampuses);
      setFilieres(filieresData);
      setSessionTypes(sessionTypesData.filter(st => st.isActive));

      // Set current academic year
      const currentYear = yearsData.find((y: AcademicYear) => y.isCurrent);
      if (currentYear) {
        setSelectedAcademicYear(currentYear);
        // Auto-detect current semester
        setSelectedSemester(getCurrentSemester(currentYear));
      }

      // Set default campus
      if (campusData.hasMultipleCampuses && campusData.campuses.length > 0) {
        const mainCampus = campusData.campuses.find((c: Campus) => c.isMainCampus);
        setSelectedCampusId(mainCampus?.id || campusData.campuses[0].id);
      } else if (campusData.campuses.length === 1) {
        setSelectedCampusId(campusData.campuses[0].id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement');
    } finally {
      setIsLoading(false);
    }
  }, [tokens?.accessToken]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Update niveaux when filière changes
  useEffect(() => {
    if (selectedFiliereId) {
      const filiere = filieres.find(f => f.id === selectedFiliereId);
      if (filiere) {
        const niveauxArray = Array.from({ length: filiere.nombreAnnees }, (_, i) => i + 1);
        setNiveaux(niveauxArray);
        setSelectedNiveau(null);
        setSelectedOptionId(null);
        setSelectedGroupeId(null);
      }
    } else {
      setNiveaux([]);
      setSelectedNiveau(null);
      setSelectedOptionId(null);
      setSelectedGroupeId(null);
    }
  }, [selectedFiliereId, filieres]);

  // Load the filière's semesters once (drives both matières and option detection)
  useEffect(() => {
    if (!selectedFiliereId) {
      setFiliereSemesters([]);
      return;
    }
    getSemestersByFiliere(selectedFiliereId)
      .then(setFiliereSemesters)
      .catch((err) => { console.error('Failed to load semesters:', err); setFiliereSemesters([]); });
  }, [selectedFiliereId]);

  // Options applicable at the selected niveau (specialisation years)
  const selectedFiliereObj = filieres.find((f) => f.id === selectedFiliereId);
  const availableOptions = useMemo(() => {
    if (!selectedFiliereObj || !selectedNiveau) return [];
    return optionsForNiveau(selectedFiliereObj, filiereSemesters, selectedNiveau);
  }, [selectedFiliereObj, filiereSemesters, selectedNiveau]);

  // Load groupes when filière, option, niveau, or campus changes.
  // On specialisation years the groups belong to the OPTION filière.
  useEffect(() => {
    const loadGroupes = async () => {
      if (!selectedFiliereId || !selectedAcademicYear) {
        setGroupes([]);
        return;
      }
      if (availableOptions.length > 0 && !selectedOptionId) {
        setGroupes([]); // an option must be chosen first at this niveau
        return;
      }

      try {
        const data = await getGroupesByFiliere(
          selectedOptionId || selectedFiliereId,
          selectedAcademicYear.id,
          selectedCampusId || undefined
        );
        // Filter by niveau if selected
        const filtered = selectedNiveau
          ? data.filter(g => g.niveau === selectedNiveau)
          : data;
        setGroupes(filtered);
      } catch (err) {
        console.error('Failed to load groupes:', err);
        setGroupes([]);
      }
    };

    loadGroupes();
  }, [selectedFiliereId, selectedOptionId, availableOptions, selectedAcademicYear, selectedCampusId, selectedNiveau]);

  // Load matières when groupe and semester change
  useEffect(() => {
    const loadMatieres = async () => {
      if (!selectedFiliereId || !selectedGroupeId) {
        setMatieres([]);
        setSemesters([]);
        return;
      }

      setLoadingMatieres(true);
      try {
        const semestersData = filiereSemesters;
        setSemesters(semestersData);

        // Get the groupe to know its niveau
        const groupe = groupes.find(g => g.id === selectedGroupeId);
        if (!groupe) return;

        // Calculate which semesters correspond to this niveau
        // e.g., niveau 1 = S1, S2; niveau 2 = S3, S4; etc.
        const semesterNumber = (groupe.niveau - 1) * 2 + selectedSemester;

        // On specialisation years the semester exists once per option
        const candidates = semestersData.filter(s => s.number === semesterNumber);
        const matchingSemester =
          candidates.find(s => semesterOptionId(s) === (selectedOptionId || null))
          || candidates.find(s => !semesterOptionId(s))
          || candidates[0];

        if (matchingSemester) {
          // Collect all module elements from this semester
          const elements: ModuleElement[] = [];
          matchingSemester.modules.forEach(module => {
            elements.push(...module.elements);
          });
          setMatieres(elements);
        } else {
          setMatieres([]);
        }
      } catch (err) {
        console.error('Failed to load matières:', err);
        setMatieres([]);
      } finally {
        setLoadingMatieres(false);
      }
    };

    loadMatieres();
  }, [selectedFiliereId, selectedOptionId, selectedGroupeId, selectedSemester, groupes, filiereSemesters]);

  // Load professors when campus changes
  useEffect(() => {
    const loadProfessors = async () => {
      if (!selectedCampusId) {
        setProfessors([]);
        return;
      }

      setLoadingProfessors(true);
      try {
        // Fetch all users with teaching activities (role=TEACHER OR isAlsoProfessor=true)
        // This includes department heads, directors, etc. who also teach
        const staffResponse = await fetchStaffUsers({
          campusId: [selectedCampusId],
          hasTeachingActivities: true,
        });
        setProfessors(staffResponse.data);
      } catch (err) {
        console.error('Failed to load professors:', err);
        setProfessors([]);
      } finally {
        setLoadingProfessors(false);
      }
    };

    loadProfessors();
  }, [selectedCampusId]);

  // Load rooms when campus changes
  useEffect(() => {
    const loadRooms = async () => {
      if (!selectedCampusId || !tokens?.accessToken) {
        setRooms([]);
        return;
      }

      setLoadingRooms(true);
      try {
        const roomsData = await fetchTeachingRooms(tokens.accessToken, selectedCampusId);
        setRooms(roomsData);
      } catch (err) {
        console.error('Failed to load rooms:', err);
        setRooms([]);
      } finally {
        setLoadingRooms(false);
      }
    };

    loadRooms();
  }, [selectedCampusId, tokens?.accessToken]);

  // Load schedule entries
  useEffect(() => {
    const loadEntries = async () => {
      if (!selectedAcademicYear || !selectedGroupeId) {
        setEntries([]);
        return;
      }

      setLoadingEntries(true);
      try {
        const data = await scheduleService.getEntries({
          academicYearId: selectedAcademicYear.id,
          groupeId: selectedGroupeId,
          campusId: selectedCampusId || undefined,
        });
        setEntries(data);
      } catch (err) {
        console.error('Failed to load entries:', err);
        setEntries([]);
      } finally {
        setLoadingEntries(false);
      }
    };

    loadEntries();
  }, [selectedAcademicYear, selectedGroupeId, selectedCampusId]);

  // Load blocked dates (holidays & vacations) and Ramadan period for the current week
  useEffect(() => {
    const loadBlockedDates = async () => {
      if (!selectedAcademicYear) {
        setBlockedDates([]);
        setRamadanPeriod(null);
        return;
      }

      try {
        // Calculate week end date
        const weekStart = new Date(currentWeekStart);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);

        const data = await scheduleService.getBlockedDates(
          selectedAcademicYear.id,
          currentWeekStart,
          weekEnd.toISOString().split('T')[0],
          selectedCampusId || undefined
        );
        setBlockedDates(data.blockedDates);
        setRamadanPeriod(data.ramadan);
      } catch (err) {
        console.error('Failed to load blocked dates:', err);
        setBlockedDates([]);
        setRamadanPeriod(null);
      }
    };

    loadBlockedDates();
  }, [selectedAcademicYear, currentWeekStart, selectedCampusId]);

  // Load semester-wide blocked dates for session planning modal
  useEffect(() => {
    const loadSemesterBlockedDates = async () => {
      if (!selectedAcademicYear) {
        setSemesterBlockedDates([]);
        return;
      }

      // Determine semester dates
      const semesterStart = selectedSemester === 1
        ? selectedAcademicYear.semester1StartDate || selectedAcademicYear.startDate
        : selectedAcademicYear.semester2StartDate || selectedAcademicYear.startDate;
      const semesterEnd = selectedSemester === 1
        ? selectedAcademicYear.semester1EndDate || selectedAcademicYear.endDate
        : selectedAcademicYear.semester2EndDate || selectedAcademicYear.endDate;

      if (!semesterStart || !semesterEnd) {
        setSemesterBlockedDates([]);
        return;
      }

      try {
        const data = await scheduleService.getBlockedDates(
          selectedAcademicYear.id,
          semesterStart,
          semesterEnd,
          selectedCampusId || undefined
        );
        setSemesterBlockedDates(data.blockedDates);
        // Also update Ramadan period if not already set
        if (data.ramadan && !ramadanPeriod) {
          setRamadanPeriod(data.ramadan);
        }
      } catch (err) {
        console.error('Failed to load semester blocked dates:', err);
        setSemesterBlockedDates([]);
      }
    };

    loadSemesterBlockedDates();
  }, [selectedAcademicYear, selectedSemester, selectedCampusId, ramadanPeriod]);

  // Load canceled sessions for the current week
  useEffect(() => {
    const loadCanceledSessions = async () => {
      if (!selectedGroupeId || !selectedAcademicYear) {
        setCanceledSessions([]);
        return;
      }

      try {
        // Calculate week end date
        const weekStart = new Date(currentWeekStart);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);

        const sessions = await scheduleService.getCanceledSessions(
          selectedGroupeId,
          currentWeekStart,
          weekEnd.toISOString().split('T')[0],
          selectedAcademicYear.id
        );

        // Transform to CanceledSession format
        const transformed: CanceledSession[] = sessions.map((session: ScheduleSession) => ({
          id: session.id,
          date: session.date,
          startTime: session.startTime,
          endTime: session.endTime,
          cancellationReason: session.cancellationReason,
          moduleElementName: session.scheduleEntry?.moduleElement?.name,
          sessionTypeName: session.scheduleEntry?.sessionType?.name,
          sessionTypeColor: session.scheduleEntry?.sessionType?.color,
        }));

        setCanceledSessions(transformed);
      } catch (err) {
        console.error('Failed to load canceled sessions:', err);
        setCanceledSessions([]);
      }
    };

    loadCanceledSessions();
  }, [selectedGroupeId, selectedAcademicYear, currentWeekStart]);

  // Calculate scheduled hours per matière from existing entries
  const scheduledHoursMap = useMemo(() => scheduledHoursByMatiere(entries, ramadanPeriod), [entries, ramadanPeriod]);

  // Handle drag start
  const handleDragStart = (matiere: ModuleElement) => {
    setDraggedMatiere(matiere);
  };

  // Handle drag end
  const handleDragEnd = () => {
    setDraggedMatiere(null);
    setDropTarget(null);
  };

  // Helper to get the date for a specific day of week in the current week
  const getDateForDayOfWeek = (dayOfWeek: number): string => {
    const date = new Date(currentWeekStart);
    const currentDayOfWeek = date.getDay();
    // Normalize both sides to a Monday=1..Sunday=7 offset so Sunday maps to the
    // end of the displayed week, not the Sunday before it
    const targetOffset = dayOfWeek === 0 ? 7 : dayOfWeek;
    const currentOffset = currentDayOfWeek === 0 ? 7 : currentDayOfWeek;
    date.setDate(date.getDate() + (targetOffset - currentOffset));
    return date.toISOString().split('T')[0];
  };

  // Check if a day is blocked
  const isDateBlocked = (dayOfWeek: number): BlockedDate | undefined => {
    const dateStr = getDateForDayOfWeek(dayOfWeek);
    return blockedDates.find(b => b.date === dateStr);
  };

  // Bounds of the currently selected semester (YYYY-MM-DD)
  const getSemesterBounds = (): { start: string; end: string } | null => {
    if (!selectedAcademicYear) return null;
    const start = selectedSemester === 1
      ? (selectedAcademicYear.semester1StartDate || selectedAcademicYear.startDate)
      : (selectedAcademicYear.semester2StartDate || selectedAcademicYear.startDate);
    const end = selectedSemester === 1
      ? (selectedAcademicYear.semester1EndDate || selectedAcademicYear.endDate)
      : (selectedAcademicYear.semester2EndDate || selectedAcademicYear.endDate);
    if (!start || !end) return null;
    return { start: String(start).split('T')[0], end: String(end).split('T')[0] };
  };

  const isDateOutOfSemester = (dateStr: string): boolean => {
    const bounds = getSemesterBounds();
    if (!bounds) return false;
    return dateStr < bounds.start || dateStr > bounds.end;
  };

  // Route a slot selection either straight to the session modal, or through the
  // out-of-semester confirmation prompt when the clicked date is out of bounds
  const openSessionModal = (data: { matiere: ModuleElement; dayOfWeek: number; startTime: string; endTime: string; date: string }) => {
    if (isDateOutOfSemester(data.date)) {
      setOutOfSemesterPrompt(data);
    } else {
      setPendingScheduleData(data);
      setShowSessionTypeModal(true);
    }
  };

  // Handle drop on time slot
  const handleDrop = (dayOfWeek: number, startTime: string) => {
    if (!draggedMatiere || !canManage) return;

    // Check if the day is blocked
    const blocked = isDateBlocked(dayOfWeek);
    if (blocked) {
      setError(`Impossible de planifier une séance: ${blocked.type === 'HOLIDAY' ? 'Jour férié' : 'Période de vacances'} - ${blocked.name}`);
      handleDragEnd();
      return;
    }

    // Calculate end time (default 2 hours)
    const [startHour, startMin] = startTime.split(':').map(Number);
    const endHour = startHour + 2;
    const endTime = `${endHour.toString().padStart(2, '0')}:${startMin.toString().padStart(2, '0')}`;

    openSessionModal({
      matiere: draggedMatiere,
      dayOfWeek,
      startTime,
      endTime,
      date: getDateForDayOfWeek(dayOfWeek),
    });
    handleDragEnd();
  };

  // Handle cell click (alternative to drag)
  const handleCellClick = (dayOfWeek: number, startTime: string) => {
    if (!canManage) return;

    // Check if the day is blocked
    const blocked = isDateBlocked(dayOfWeek);
    if (blocked) {
      setError(`Impossible de planifier une séance: ${blocked.type === 'HOLIDAY' ? 'Jour férié' : 'Période de vacances'} - ${blocked.name}`);
      return;
    }

    // If a matiere is selected, use it
    // Otherwise, show a matiere selection first
    if (matieres.length === 0) {
      setError('Veuillez d\'abord sélectionner un groupe avec des matières');
      return;
    }

    // Calculate end time
    const [startHour, startMin] = startTime.split(':').map(Number);
    const endHour = startHour + 2;
    const endTime = `${endHour.toString().padStart(2, '0')}:${startMin.toString().padStart(2, '0')}`;

    // Open a picker if needed, or use first matière as default
    // For now, just open the session modal with first matière
    openSessionModal({
      matiere: matieres[0],
      dayOfWeek,
      startTime,
      endTime,
      date: getDateForDayOfWeek(dayOfWeek),
    });
  };

  // Build the creation payload once, for both the conflict pre-check and the save
  const buildCreateDto = (
    sessionTypeId: string,
    selectedMatiereId?: string,
    selectedProfessorId?: string,
    recurrenceMode?: 'single' | 'until_volume' | 'custom',
    recurrenceWeeks?: number,
    roomId?: string,
    ramadanStartTime?: string,
    ramadanEndTime?: string,
    confirmVolumeExceeded?: boolean,
    paidDurationMinutes?: number
  ): CreateScheduleEntryDto | null => {
    if (!pendingScheduleData || !selectedAcademicYear || !selectedCampusId || !selectedFiliereId || !selectedGroupeId || !selectedProfessorId) {
      return null;
    }

    const matiereToUse = selectedMatiereId
      ? matieres.find(m => m.id === selectedMatiereId) || pendingScheduleData.matiere
      : pendingScheduleData.matiere;

    const sessionType = sessionTypes.find(st => st.id === sessionTypeId);
    const durationMinutes = sessionType?.durationMinutes || 120;

    // End time follows the session type duration
    const [startHour, startMin] = pendingScheduleData.startTime.split(':').map(Number);
    const endMinutes = startHour * 60 + startMin + durationMinutes;
    const endTime = `${Math.floor(endMinutes / 60).toString().padStart(2, '0')}:${(endMinutes % 60).toString().padStart(2, '0')}`;

    const isRecurring = recurrenceMode !== 'single';
    let weeks: number | undefined;
    if (recurrenceMode === 'until_volume' && matiereToUse.volumeHoraire) {
      weeks = Math.ceil(matiereToUse.volumeHoraire / (durationMinutes / 60));
    } else if (recurrenceMode === 'custom' && recurrenceWeeks) {
      weeks = recurrenceWeeks;
    }

    return {
      academicYearId: selectedAcademicYear.id,
      campusId: selectedCampusId,
      filiereId: selectedOptionId || selectedFiliereId,
      groupeId: selectedGroupeId,
      moduleElementId: matiereToUse.id,
      professorId: selectedProfessorId,
      roomId: roomId || undefined,
      sessionTypeId,
      dayOfWeek: pendingScheduleData.dayOfWeek,
      startTime: pendingScheduleData.startTime,
      endTime,
      isRecurring,
      recurrenceWeeks: weeks,
      ramadanStartTime: ramadanStartTime || undefined,
      ramadanEndTime: ramadanEndTime || undefined,
      semester: selectedSemester,
      startDate: pendingScheduleData.date,
      allowOutOfSemester: pendingScheduleData.outOfSemester || undefined,
      confirmVolumeExceeded: confirmVolumeExceeded || undefined,
      paidDurationMinutes: paidDurationMinutes || undefined,
    };
  };

  // Handle session type selection and create entry (with conflict checking)
  const handleCreateEntry = async (
    sessionTypeId: string,
    selectedMatiereId?: string,
    selectedProfessorId?: string,
    recurrenceMode?: 'single' | 'until_volume' | 'custom',
    recurrenceWeeks?: number,
    roomId?: string,
    ramadanStartTime?: string,
    ramadanEndTime?: string,
    paidDurationMinutes?: number
  ) => {
    // A double click must not start a second creation
    if (submitInFlightRef.current) return;

    // Clear previous messages
    setError(null);
    setSuccess(null);

    if (!pendingScheduleData || !selectedAcademicYear || !selectedCampusId || !selectedFiliereId || !selectedGroupeId) {
      setError('Données manquantes pour créer la séance');
      return;
    }

    if (!selectedProfessorId) {
      setError('Veuillez sélectionner un professeur');
      return;
    }

    submitInFlightRef.current = true;
    setIsSubmittingEntry(true);

    const createData = buildCreateDto(
      sessionTypeId,
      selectedMatiereId,
      selectedProfessorId,
      recurrenceMode,
      recurrenceWeeks,
      roomId,
      ramadanStartTime,
      ramadanEndTime,
      false,
      paidDurationMinutes
    );
    if (!createData) {
      setError('Données manquantes pour créer la séance');
      submitInFlightRef.current = false;
      setIsSubmittingEntry(false);
      return;
    }

    const pending = {
      sessionTypeId,
      selectedMatiereId,
      selectedProfessorId,
      recurrenceMode,
      recurrenceWeeks,
      roomId,
      ramadanStartTime,
      ramadanEndTime,
      paidDurationMinutes,
    };

    try {
      // Check conflicts on the REAL dates the sessions would occupy
      const conflictCheck = await scheduleService.checkConflicts(createData);

      // If there are conflicts, show the conflict resolution modal
      if (conflictCheck.hasConflicts) {
        setPendingCreateData(pending);
        setConflictResult(conflictCheck);
        setShowConflictModal(true);
        // Don't close the session type modal yet
        return;
      }

      // Beyond the subject's volume horaire: ask first
      if (conflictCheck.volume?.exceeds) {
        setPendingCreateData(pending);
        setVolumeConfirm({
          volume: conflictCheck.volume,
          matiereName: matieres.find(m => m.id === createData.moduleElementId)?.name || pendingScheduleData.matiere.name,
        });
        return;
      }

      // No conflicts - proceed with creation
      await executeCreateEntry(
        sessionTypeId,
        selectedMatiereId,
        selectedProfessorId,
        recurrenceMode,
        recurrenceWeeks,
        roomId,
        false, // not merging groups
        ramadanStartTime,
        ramadanEndTime,
        false,
        paidDurationMinutes
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la vérification des conflits');
      setShowSessionTypeModal(false);
      setPendingScheduleData(null);
    } finally {
      submitInFlightRef.current = false;
      setIsSubmittingEntry(false);
    }
  };

  // Volume horaire confirmed: create with the explicit flag
  const handleConfirmVolumeExceeded = async () => {
    if (!pendingCreateData) return;
    const data = pendingCreateData;
    setVolumeConfirm(null);
    await executeCreateEntry(
      data.sessionTypeId,
      data.selectedMatiereId,
      data.selectedProfessorId,
      data.recurrenceMode,
      data.recurrenceWeeks,
      data.roomId,
      false,
      data.ramadanStartTime,
      data.ramadanEndTime,
      true, // volume exceeded, explicitly accepted
      data.paidDurationMinutes
    );
  };

  const handleCancelVolumeExceeded = () => {
    setVolumeConfirm(null);
    setPendingCreateData(null);
    // Keep the session type modal open so the user can adjust the recurrence
  };

  // Execute the actual entry creation (after conflict check)
  const executeCreateEntry = async (
    sessionTypeId: string,
    selectedMatiereId?: string,
    selectedProfessorId?: string,
    recurrenceMode?: 'single' | 'until_volume' | 'custom',
    recurrenceWeeks?: number,
    roomId?: string,
    mergeGroups: boolean = false,
    ramadanStartTime?: string,
    ramadanEndTime?: string,
    confirmVolumeExceeded: boolean = false,
    paidDurationMinutes?: number,
    // Create the series without the clashing weeks (confirmed in the conflict dialog)
    skipConflicts: boolean = false
  ) => {
    if (!pendingScheduleData || !selectedAcademicYear || !selectedCampusId || !selectedFiliereId || !selectedGroupeId || !selectedProfessorId) {
      setError('Données manquantes pour créer la séance');
      return;
    }

    setIsCreatingEntry(true);

    const createData = buildCreateDto(
      sessionTypeId,
      selectedMatiereId,
      selectedProfessorId,
      recurrenceMode,
      recurrenceWeeks,
      roomId,
      ramadanStartTime,
      ramadanEndTime,
      confirmVolumeExceeded,
      paidDurationMinutes
    );
    if (!createData) {
      setError('Données manquantes pour créer la séance');
      setIsCreatingEntry(false);
      return;
    }
    if (skipConflicts) createData.skipConflictingDates = true;

    try {
      // Merging = joining the existing course's shared session: the new course
      // is created with its own subject and attendance list, on the same dates,
      // same professor, ONE room (the existing one, or the proposed one)
      if (mergeGroups && conflictResult?.canCombineGroups) {
        const professorConflict = conflictResult.conflicts.find(c => c.type === 'PROFESSOR');
        const existingEntryId = conflictResult.mergeTarget?.entryId ?? professorConflict?.conflictingEntryId;

        if (existingEntryId) {
          const newEntry = await scheduleService.shareEntry(existingEntryId, {
            ...createData,
            roomId: conflictResult.mergeTarget?.roomIdToAssign ?? createData.roomId,
          });
          setSuccess(
            `Séance partagée créée : ${newEntry.groupe?.name ?? 'le groupe'} suit désormais ce créneau avec ` +
            `${conflictResult.mergeTarget?.existingGroupNames.join(', ') || "l'autre groupe"}`
          );
          setEntries(prev => [...prev, newEntry]);
        }
      } else {
        const newEntry = await scheduleService.createEntry(createData);
        const skipped = skipConflicts && conflictResult?.skipOption
          ? conflictResult.skipOption.plannedCount - conflictResult.skipOption.remainingCount
          : 0;
        setSuccess(
          skipped > 0
            ? `Cours planifié : ${newEntry.recurrenceWeeks ?? ''} séance(s) créée(s), ${skipped} semaine(s) en conflit sautée(s)`
            : 'Séance créée avec succès',
        );

        // Update entries with the new entry immediately
        setEntries(prev => [...prev, newEntry]);
      }

      // Reload all entries for consistency
      try {
        const data = await scheduleService.getEntries({
          academicYearId: selectedAcademicYear.id,
          groupeId: selectedGroupeId,
          campusId: selectedCampusId,
        });
        setEntries(data);
      } catch (refetchError) {
        console.warn('Failed to refetch entries:', refetchError);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la création');
    } finally {
      setIsCreatingEntry(false);
      setShowSessionTypeModal(false);
      setShowConflictModal(false);
      setPendingScheduleData(null);
      setPendingCreateData(null);
      setConflictResult(null);
    }
  };

  // Handle merge groups from conflict modal
  const handleMergeGroups = async () => {
    if (!pendingCreateData) return;

    await executeCreateEntry(
      pendingCreateData.sessionTypeId,
      pendingCreateData.selectedMatiereId,
      pendingCreateData.selectedProfessorId,
      pendingCreateData.recurrenceMode,
      pendingCreateData.recurrenceWeeks,
      pendingCreateData.roomId,
      true, // merge groups
      pendingCreateData.ramadanStartTime,
      pendingCreateData.ramadanEndTime
    );
  };

  // Plan around the clashing weeks, as confirmed in the conflict modal
  const handleSkipConflicts = async () => {
    if (!pendingCreateData) return;
    await executeCreateEntry(
      pendingCreateData.sessionTypeId,
      pendingCreateData.selectedMatiereId,
      pendingCreateData.selectedProfessorId,
      pendingCreateData.recurrenceMode,
      pendingCreateData.recurrenceWeeks,
      pendingCreateData.roomId,
      false,
      pendingCreateData.ramadanStartTime,
      pendingCreateData.ramadanEndTime,
      false,
      pendingCreateData.paidDurationMinutes,
      true,
    );
  };

  // Handle cancel from conflict modal
  const handleCancelConflict = () => {
    setShowConflictModal(false);
    setConflictResult(null);
    setPendingCreateData(null);
    // Keep the session type modal open so user can adjust
  };

  // Handle entry click — the edit modal only offers mutations, so gate it on SCHEDULE_MANAGE
  const handleEntryClick = (entry: ScheduleEntry, sessionDate?: string) => {
    if (!canManage) return;
    setSelectedEntry(entry);
    setSelectedSessionDate(sessionDate || null);
    setShowEditModal(true);
  };

  // Handle entry update (reload entries after modification)
  const handleEntryUpdate = async () => {
    if (!selectedAcademicYear || !selectedGroupeId) return;
    try {
      const data = await scheduleService.getEntries({
        academicYearId: selectedAcademicYear.id,
        groupeId: selectedGroupeId,
        campusId: selectedCampusId || undefined,
      });
      setEntries(data);
    } catch (err) {
      console.error('Failed to reload entries:', err);
    }
  };

  // Week navigation
  const handlePreviousWeek = () => {
    const current = new Date(currentWeekStart);
    current.setDate(current.getDate() - 7);
    setCurrentWeekStart(current.toISOString().split('T')[0]);
  };

  const handleNextWeek = () => {
    const current = new Date(currentWeekStart);
    current.setDate(current.getDate() + 7);
    setCurrentWeekStart(current.toISOString().split('T')[0]);
  };

  const handleToday = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    setCurrentWeekStart(new Date(today.setDate(diff)).toISOString().split('T')[0]);
  };

  const formatWeekRange = () => {
    const start = new Date(currentWeekStart);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    const dayMonth: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
    const dayMonthYear: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' };
    // Show the year on both sides when the week spans two years
    if (start.getFullYear() !== end.getFullYear()) {
      return `${start.toLocaleDateString('fr-FR', dayMonthYear)} - ${end.toLocaleDateString('fr-FR', dayMonthYear)}`;
    }
    return `${start.toLocaleDateString('fr-FR', dayMonth)} - ${end.toLocaleDateString('fr-FR', dayMonthYear)}`;
  };

  // Jump directly to the week containing a picked date
  const weekPickerRef = useRef<HTMLInputElement>(null);
  const handleWeekPick = (value: string) => {
    if (!value) return;
    const picked = new Date(value + 'T12:00:00');
    if (isNaN(picked.getTime())) return;
    const day = picked.getDay();
    const diff = picked.getDate() - day + (day === 0 ? -6 : 1); // Monday of that week
    picked.setDate(diff);
    setCurrentWeekStart(picked.toISOString().split('T')[0]);
  };
  const openWeekPicker = () => {
    const input = weekPickerRef.current;
    if (!input) return;
    if (typeof input.showPicker === 'function') {
      input.showPicker();
    } else {
      input.click();
    }
  };

  // Filter filières by search
  const filteredFilieres = filieres.filter((f) => {
    const query = searchQuery.toLowerCase();
    return (
      f.filiere.toLowerCase().includes(query) ||
      f.codeFiliere.toLowerCase().includes(query) ||
      f.diplome.toLowerCase().includes(query)
    );
  });

  // Get selected groupe
  const selectedGroupe = groupes.find((g) => g.id === selectedGroupeId);

  // Clear messages after timeout
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [error]);

  const getNiveauLabel = (niveau: number): string => {
    const suffixes: Record<number, string> = {
      1: '1ère Année',
      2: '2ème Année',
      3: '3ème Année',
      4: '4ème Année',
      5: '5ème Année',
    };
    return suffixes[niveau] || `${niveau}ème Année`;
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50 overflow-hidden">
      {/* Header - Compact */}
      <div className="bg-white border-b border-gray-200 px-4 py-2 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-primary-50 rounded-lg">
              <CalendarClock className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <h1 className="text-base font-semibold text-gray-900">
                Gestion des Emplois du Temps
              </h1>
              <p className="text-xs text-gray-500">
                Planifiez et gérez les séances de cours
              </p>
            </div>
          </div>

          {/* Top Selectors */}
          <div className="flex items-center gap-3">
            <RoomOccupancyButton
              campuses={campuses}
              campusId={selectedCampusId}
              date={new Date(`${currentWeekStart}T12:00:00`)}
              academicYearId={selectedAcademicYear?.id}
              planning={selectedAcademicYear ? { academicYear: selectedAcademicYear, semester: selectedSemester } : null}
            />

            {/* Campus Selector */}
            {hasMultipleCampuses && campuses.length > 1 && (
              <div className="flex items-center gap-1.5">
                <Building2 className="h-3.5 w-3.5 text-gray-500" />
                <select
                  value={selectedCampusId || ''}
                  onChange={(e) => setSelectedCampusId(e.target.value)}
                  className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-primary-500 bg-blue-50"
                >
                  {campuses.map((campus) => (
                    <option key={campus.id} value={campus.id}>
                      {campus.sigle || campus.name} {campus.isMainCampus ? '(Principal)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Academic Year Selector */}
            <select
              value={selectedAcademicYear?.id || ''}
              onChange={(e) => {
                const year = academicYears.find(y => y.id === e.target.value);
                setSelectedAcademicYear(year || null);
                if (year) {
                  setSelectedSemester(getCurrentSemester(year));
                }
              }}
              className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {academicYears.map((year) => (
                <option key={year.id} value={year.id}>
                  {year.name} {year.isCurrent ? '(Courante)' : ''}
                </option>
              ))}
            </select>

            {/* Semester Toggle */}
            <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setSelectedSemester(1)}
                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                  selectedSemester === 1
                    ? 'bg-white text-primary-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                S1
              </button>
              <button
                onClick={() => setSelectedSemester(2)}
                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                  selectedSemester === 2
                    ? 'bg-white text-primary-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                S2
              </button>
            </div>

            {/* Progress Panel Toggle */}
            <button
              onClick={() => setShowProgressPanel(!showProgressPanel)}
              className={`p-1.5 rounded-lg transition-colors ${
                showProgressPanel
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
              }`}
              title={showProgressPanel ? 'Masquer la progression' : 'Afficher la progression'}
            >
              <BarChart3 className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Alerts - Compact */}
      {error && (
        <div className="mx-4 mt-2 p-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-xs flex-shrink-0">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {success && (
        <div className="mx-4 mt-2 p-2 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700 text-xs flex-shrink-0">
          <CheckCircle className="h-4 w-4 flex-shrink-0" />
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} className="ml-auto">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Main Content - Fills remaining space */}
      <div className="flex-1 flex overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center flex-1">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        ) : (
          <div className="flex-1 p-3 overflow-hidden">
            <div className="grid grid-cols-12 gap-3 h-full">
            {/* Left Sidebar - Filters and Matières */}
            <aside className="col-span-3 flex flex-col gap-2 overflow-hidden">
              {/* Filière Selection */}
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col flex-shrink-0" style={{ maxHeight: selectedGroupeId ? '45%' : '70%' }}>
                <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex-shrink-0">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-gray-700">
                    <Filter className="h-3.5 w-3.5" />
                    Sélection du groupe
                  </div>
                </div>

                {/* Search */}
                <div className="p-2 border-b border-gray-100 flex-shrink-0">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Rechercher une filière..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>

                {/* Filières List */}
                <div className="flex-1 overflow-y-auto min-h-0">
                  {filteredFilieres.length === 0 ? (
                    <div className="p-3 text-center text-gray-500 text-xs">
                      Aucune filière trouvée
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {filteredFilieres.map((filiere) => (
                        <button
                          key={filiere.id}
                          onClick={() => setSelectedFiliereId(filiere.id)}
                          className={`w-full px-3 py-2 text-left transition-colors ${
                            selectedFiliereId === filiere.id
                              ? 'bg-primary-50 border-l-3 border-primary-500'
                              : 'hover:bg-gray-50 border-l-3 border-transparent'
                          }`}
                        >
                          <div className="flex items-center gap-1.5">
                            <Layers className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-gray-900 leading-snug break-words" title={filiere.filiere}>
                                {filiere.filiere}
                              </p>
                              <p className="text-[10px] text-gray-500 leading-snug break-words">
                                {filiere.codeFiliere}{filiere.diplome ? ` • ${filiere.diplome}` : ''}
                              </p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Niveau and Groupe Selection */}
                {selectedFiliereId && (
                  <div className="p-2 space-y-2 border-t border-gray-200 bg-gray-50 flex-shrink-0">
                    {/* Niveau Selector */}
                    <div>
                      <label className="block text-[10px] font-medium text-gray-500 mb-0.5">
                        Niveau
                      </label>
                      <select
                        value={selectedNiveau || ''}
                        onChange={(e) => {
                          setSelectedNiveau(e.target.value ? parseInt(e.target.value) : null);
                          setSelectedOptionId(null);
                          setSelectedGroupeId(null);
                        }}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">Tous les niveaux</option>
                        {niveaux.map((niveau) => (
                          <option key={niveau} value={niveau}>
                            {getNiveauLabel(niveau)}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Option Selector — only when the selected niveau has options */}
                    {availableOptions.length > 0 && (
                      <div>
                        <label className="block text-[10px] font-medium text-gray-500 mb-0.5">
                          Option
                        </label>
                        <select
                          value={selectedOptionId || ''}
                          onChange={(e) => {
                            setSelectedOptionId(e.target.value || null);
                            setSelectedGroupeId(null);
                          }}
                          className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                          <option value="">Sélectionner une option</option>
                          {availableOptions.map((o) => (
                            <option key={o.id} value={o.id}>
                              {o.optionName || o.filiere} ({o.codeFiliere})
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Groupe Selector */}
                    <div>
                      <label className="block text-[10px] font-medium text-gray-500 mb-0.5">
                        Groupe
                      </label>
                      <select
                        value={selectedGroupeId || ''}
                        onChange={(e) => setSelectedGroupeId(e.target.value || null)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">Sélectionner un groupe</option>
                        {groupes.map((groupe) => (
                          <option key={groupe.id} value={groupe.id}>
                            {groupe.name} ({groupe.studentCount || 0} étudiants)
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* Matières Panel */}
              {selectedGroupeId && (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden flex-1 flex flex-col min-h-0">
                  <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex-shrink-0">
                    <div className="flex items-center gap-1.5 text-xs font-medium text-gray-700">
                      <BookOpen className="h-3.5 w-3.5" />
                      Matières - S{selectedGroupe ? (selectedGroupe.niveau - 1) * 2 + selectedSemester : selectedSemester}
                    </div>
                    <p className="text-[10px] text-gray-500 mt-0.5">
                      Glissez une matière sur l'emploi du temps
                    </p>
                  </div>

                  <div className="flex-1 overflow-y-auto min-h-0">
                    {loadingMatieres ? (
                      <div className="p-3 flex justify-center">
                        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                      </div>
                    ) : matieres.length === 0 ? (
                      <div className="p-3 text-center text-gray-500 text-xs">
                        Aucune matière pour ce semestre
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {matieres.map((matiere) => {
                          // Hours already scheduled, counted in PAID hours (durée rémunérée),
                          // the same total the planning modal and the progress panel use
                          const scheduledHours = scheduledHoursMap.get(matiere.id) || 0;
                          const volumeHoraire = matiere.volumeHoraire || 0;

                          // Status: green = fully scheduled, orange = partially, gray = not started
                          let statusColor = 'bg-gray-300'; // not scheduled
                          let statusText = 'Non planifié';
                          if (scheduledHours > 0 && volumeHoraire > 0) {
                            if (scheduledHours >= volumeHoraire) {
                              statusColor = 'bg-green-500';
                              statusText = 'Complet';
                            } else {
                              statusColor = 'bg-amber-500';
                              statusText = `${Math.round(scheduledHours)}/${volumeHoraire}h`;
                            }
                          } else if (scheduledHours > 0) {
                            statusColor = 'bg-green-500';
                            statusText = `${Math.round(scheduledHours)}h`;
                          }

                          return (
                            <div
                              key={matiere.id}
                              id={`matiere-${matiere.id}`}
                              draggable={canManage}
                              onDragStart={() => handleDragStart(matiere)}
                              onDragEnd={handleDragEnd}
                              className={`px-3 py-2 flex items-center gap-2 cursor-grab hover:bg-gray-50 transition-all ${
                                draggedMatiere?.id === matiere.id ? 'opacity-50 bg-primary-50' : ''
                              }`}
                            >
                              {/* Status indicator */}
                              <div
                                className={`w-2 h-2 rounded-full flex-shrink-0 ${statusColor}`}
                                title={statusText}
                              />
                              <GripVertical className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-gray-900 leading-snug line-clamp-2" title={matiere.name}>
                                  {matiere.name}
                                </p>
                                <div className="flex items-center gap-2 text-[10px] text-gray-500">
                                  <span>Coef. {matiere.weight}%</span>
                                  {volumeHoraire > 0 && (
                                    <span className={scheduledHours >= volumeHoraire ? 'text-green-600' : scheduledHours > 0 ? 'text-amber-600' : ''}>
                                      {Math.round(scheduledHours)}/{volumeHoraire}h
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Quick Tips */}
              {!selectedGroupeId && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex-shrink-0">
                  <h4 className="text-xs font-medium text-blue-800 mb-1.5">
                    Comment utiliser
                  </h4>
                  <ul className="text-[10px] text-blue-700 space-y-0.5">
                    <li>1. Sélectionnez une filière</li>
                    <li>2. Choisissez le niveau et le groupe</li>
                    <li>3. Glissez les matières sur la grille</li>
                    <li>4. Ou cliquez sur une case pour ajouter</li>
                  </ul>
                </div>
              )}
            </aside>

            {/* Main Content - Schedule Grid */}
            <main className="col-span-9 flex flex-col overflow-hidden">
              {/* Week Navigation */}
              <div className="flex items-center justify-between mb-2 bg-white rounded-lg border border-gray-200 p-2 flex-shrink-0">
                <button
                  onClick={handlePreviousWeek}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ChevronLeft size={16} />
                </button>

                <div className="flex items-center gap-3">
                  <div className="relative">
                    <button
                      onClick={openWeekPicker}
                      title="Aller à une date"
                      className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 hover:bg-gray-100 px-2 py-1 rounded-lg transition-colors"
                    >
                      <CalendarClock size={14} className="text-gray-400" />
                      {formatWeekRange()}
                    </button>
                    <input
                      ref={weekPickerRef}
                      type="date"
                      value={currentWeekStart}
                      onChange={(e) => handleWeekPick(e.target.value)}
                      className="absolute bottom-0 left-0 w-px h-px opacity-0 pointer-events-none"
                      tabIndex={-1}
                      aria-hidden="true"
                    />
                  </div>
                  <button
                    onClick={handleToday}
                    className="px-2 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                  >
                    Aujourd'hui
                  </button>
                </div>

                <button
                  onClick={handleNextWeek}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ChevronRight size={16} />
                </button>
              </div>

              {/* Schedule Grid */}
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden flex-1 min-h-0">
                {!selectedGroupeId ? (
                  <div className="p-8 text-center h-full flex flex-col items-center justify-center">
                    <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                      <Users className="h-6 w-6 text-gray-400" />
                    </div>
                    <h3 className="text-sm font-medium text-gray-900 mb-1">
                      Sélectionnez un groupe
                    </h3>
                    <p className="text-xs text-gray-500">
                      Choisissez une filière et un groupe pour voir et modifier son emploi du temps
                    </p>
                  </div>
                ) : (
                  <TimetableGrid
                    entries={entries}
                    canceledSessions={canceledSessions}
                    weekStart={currentWeekStart}
                    onEntryClick={handleEntryClick}
                    onCellClick={canManage ? handleCellClick : undefined}
                    onDrop={canManage ? handleDrop : undefined}
                    draggedMatiere={draggedMatiere}
                    dropTarget={dropTarget}
                    setDropTarget={setDropTarget}
                    loading={loadingEntries}
                    blockedDates={blockedDates}
                    ramadanPeriod={ramadanPeriod}
                  />
                )}
              </div>
            </main>
            </div>
          </div>
        )}

        {/* Progress Panel */}
        {showProgressPanel && selectedGroupeId && (
          <SchedulingProgressPanel
            matieres={matieres}
            scheduledHoursMap={scheduledHoursMap}
            groupName={groupes.find(g => g.id === selectedGroupeId)?.name}
            onMatiereClick={(matiere) => {
              // Scroll to or highlight the matiere in the list
              const element = document.getElementById(`matiere-${matiere.id}`);
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                element.classList.add('ring-2', 'ring-primary-500');
                setTimeout(() => {
                  element.classList.remove('ring-2', 'ring-primary-500');
                }, 2000);
              }
            }}
          />
        )}
      </div>

      {/* Session Type Modal */}
      {showSessionTypeModal && pendingScheduleData && (
        <SessionTypeModal
          matieres={matieres}
          selectedMatiere={pendingScheduleData.matiere}
          sessionTypes={sessionTypes}
          professors={professors}
          rooms={rooms}
          semesterStartDate={
            selectedSemester === 1
              ? (selectedAcademicYear?.semester1StartDate || selectedAcademicYear?.startDate)
              : (selectedAcademicYear?.semester2StartDate || selectedAcademicYear?.startDate)
          }
          semesterEndDate={
            selectedSemester === 1
              ? (selectedAcademicYear?.semester1EndDate || selectedAcademicYear?.endDate)
              : (selectedAcademicYear?.semester2EndDate || selectedAcademicYear?.endDate)
          }
          selectedDayOfWeek={pendingScheduleData.dayOfWeek}
          selectedStartTime={pendingScheduleData.startTime}
          selectedDate={pendingScheduleData.date}
          outOfSemester={pendingScheduleData.outOfSemester}
          scheduledHoursMap={scheduledHoursMap}
          blockedDates={semesterBlockedDates}
          ramadanPeriod={ramadanPeriod}
          isSubmitting={isSubmittingEntry || isCreatingEntry}
          onConfirm={handleCreateEntry}
          onClose={() => {
            setShowSessionTypeModal(false);
            setPendingScheduleData(null);
          }}
        />
      )}

      {/* Edit Session Modal */}
      {showEditModal && selectedEntry && selectedAcademicYear && (
        <EditSessionModal
          entry={selectedEntry}
          sessionTypes={sessionTypes}
          professors={professors}
          rooms={rooms}
          academicYearId={selectedAcademicYear.id}
          preselectedSessionDate={selectedSessionDate || undefined}
          onClose={() => {
            setShowEditModal(false);
            setSelectedEntry(null);
            setSelectedSessionDate(null);
          }}
          onUpdate={handleEntryUpdate}
          onError={setError}
          onSuccess={setSuccess}
        />
      )}

      {/* Out-of-semester confirmation */}
      {outOfSemesterPrompt && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-3">
              <div className="p-2.5 bg-amber-100 rounded-full flex-shrink-0">
                <AlertCircle className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Hors du semestre</h3>
                <p className="text-sm text-gray-600 mt-1.5">
                  La date choisie{' '}
                  <span className="font-medium text-gray-900">
                    {new Date(outOfSemesterPrompt.date + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  </span>{' '}
                  est en dehors du Semestre {selectedSemester}
                  {(() => {
                    const b = getSemesterBounds();
                    return b
                      ? ` (du ${new Date(b.start + 'T12:00:00').toLocaleDateString('fr-FR')} au ${new Date(b.end + 'T12:00:00').toLocaleDateString('fr-FR')})`
                      : '';
                  })()}
                  .
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  Voulez-vous quand même planifier cette séance en dehors des bornes du semestre ?
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setOutOfSemesterPrompt(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={() => {
                  setPendingScheduleData({ ...outOfSemesterPrompt, outOfSemester: true });
                  setShowSessionTypeModal(true);
                  setOutOfSemesterPrompt(null);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors"
              >
                Continuer quand même
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Volume horaire confirmation */}
      {volumeConfirm && (
        <VolumeExceededModal
          matiereName={volumeConfirm.matiereName}
          volume={volumeConfirm.volume}
          isLoading={isCreatingEntry}
          onConfirm={handleConfirmVolumeExceeded}
          onCancel={handleCancelVolumeExceeded}
        />
      )}

      {/* Conflict Resolution Modal */}
      {showConflictModal && conflictResult && (
        <ConflictResolutionModal
          conflictResult={conflictResult}
          onMergeGroups={handleMergeGroups}
          onSkipConflicts={handleSkipConflicts}
          onCancel={handleCancelConflict}
          isLoading={isCreatingEntry}
        />
      )}
    </div>
  );
};

export default TimetableManagementPage;
